import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Mail, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { API } from "../../../App";
import { formatDateSafe } from "../../../lib/formatDate";

/** Envoi d'une analyse (ABC, restructuration, fiche mémo, carte des modes…) en PDF au patient, dans sa langue, avec la marque du Cabinet. */
export const AnalysisEmailDialog = ({ open, onOpenChange, report, analysisKey, patientId = "", patientLabel = "", onSent }) => {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const url = `${API}/session-reports/${report.report_id}/analyses/${analysisKey}/email`;

  useEffect(() => {
    if (!open) return;
    setPreview(null); setIntro(""); setSubject("");
    axios.post(`${url}/preview`, { patient_id: patientId }, { withCredentials: true })
      .then((r) => { setPreview(r.data); setTo((r.data.to || []).join(", ")); setSubject(r.data.subject || ""); })
      .catch((e) => toast.error(e.response?.data?.detail || "Impossible de préparer l'e-mail"));
  }, [open, report.report_id, analysisKey, patientId]);

  const refresh = async () => {
    setBusy("preview");
    try { const r = await axios.post(`${url}/preview`, { to, subject, intro, patient_id: patientId }, { withCredentials: true }); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  };

  const send = async () => {
    if (!to.trim()) { toast.error("Indiquez l'adresse e-mail du patient"); return; }
    setBusy("send");
    try {
      const r = await axios.post(url, { to, subject, intro, patient_id: patientId }, { withCredentials: true });
      toast.success(`PDF envoyé à ${r.data.sent_to.join(", ")}`);
      onSent?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="analysis-email-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5" /> Envoyer au patient{patientLabel ? ` — ${patientLabel}` : ""} : {preview?.label || "PDF"}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <label className="block">
            <span className="text-xs text-muted-foreground">Destinataire(s) — séparés par des virgules</span>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="patient@exemple.fr" data-testid="analysis-email-to" />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Objet</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="analysis-email-subject" />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Message personnalisé (optionnel, remplace le texte par défaut dans la langue du patient)</span>
            <textarea value={intro} onChange={(e) => setIntro(e.target.value)} onBlur={refresh} rows={3} className="w-full rounded-md border bg-background px-3 py-2 text-sm" placeholder="Bonjour, comme convenu voici…" data-testid="analysis-email-intro" />
          </label>
          {preview?.sent_at && <p className="text-xs text-emerald-700" data-testid="analysis-email-sent-at">Déjà envoyé le {formatDateSafe(preview.sent_at, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>}
          {preview && !preview.smtp_configured && <p className="text-xs text-amber-700" data-testid="analysis-email-no-smtp">Configuration SMTP manquante : Mon profil → Messagerie & IA.</p>}
        </div>
        {preview ? <iframe title="Aperçu" srcDoc={preview.html} sandbox="" className="w-full h-[40vh] border rounded-lg bg-[#f1f5f9]" data-testid="analysis-email-preview" />
          : <div className="h-[40vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        {preview && <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="analysis-email-attachment"><Paperclip className="w-3.5 h-3.5" /> Pièce jointe : {preview.attachment} · langue : {preview.language?.toUpperCase()} · logos, signature du Cabinet et mention RGPD · les notes réservées au thérapeute ne sont jamais envoyées.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="analysis-email-cancel">Annuler</Button>
          <Button onClick={send} disabled={busy !== "" || !preview?.smtp_configured} className="gap-1.5" data-testid="analysis-email-send">
            {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {busy === "send" ? "Envoi…" : "Envoyer au patient"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
